import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMG } from "../data/content";
import { fadeUp, viewport } from "../lib/motion";
import { useOrder } from "../context/OrderContext";

const ITEMS = [
  { img: IMG.cappuccinoReal, quote: "They have the best latte.", author: "Daniel K." },
  { img: IMG.interiorRoom, quote: "My favorite coffee shop.", author: "Maya R." },
  { img: IMG.interiorWide, quote: "I spend more time here than at home.", author: "Priya S." },
  { img: IMG.mugChandelier, quote: "Cozy, warm, and such a vibe.", author: "Jordan T." },
  { img: IMG.spread, quote: "Best brunch in Arlington.", author: "Alexis M." },
  { img: IMG.interiorBar, quote: "The staff know my name & my order.", author: "Sam D." },
  { img: IMG.almondCroissant, quote: "Fresh pastries every single morning.", author: "Leah B." },
  { img: IMG.hummus, quote: "The Mediterranean food is unreal.", author: "Omar S." },
  { img: IMG.coffeeWall, quote: "Coffee genuinely worth the detour.", author: "Nadia H." },
  { img: IMG.detourWall, quote: "I'm here every single morning.", author: "Chris P." },
];

const Slide = ({ item }) => (
  <div
    className="group/card relative shrink-0 w-[240px] sm:w-[290px] overflow-hidden rounded-2xl"
    data-testid={`slider-item-${item.author.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}
  >
    <div className="aspect-[3/4] overflow-hidden">
      <img
        src={item.img}
        alt={`Detour Coffee Co. — ${item.quote}`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover/card:scale-110"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-5">
      <span className="text-highlight font-serif text-2xl leading-none">“</span>
      <h3 className="font-serif text-lg sm:text-xl text-background leading-snug mt-0.5">{item.quote}</h3>
      <span className="mt-2 block text-highlight text-[10px] uppercase tracking-[0.22em] font-semibold">— {item.author}</span>
    </div>
  </div>
);

const SignatureSlider = () => {
  const openOrder = useOrder();
  return (
  <section
    id="drinks"
    className="relative py-14 sm:py-28 bg-[#241812] overflow-hidden"
    data-testid="signature-slider-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-highlight mb-4 flex items-center gap-3"
          >
            <span className="inline-block h-px w-8 bg-highlight" />
            Loved by locals
          </motion.p>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="text-background text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
          >
            A taste of <span className="text-highlight">Detour.</span>
          </motion.h2>
        </div>
        <motion.button
          onClick={openOrder}
          data-testid="slider-order-btn"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="group inline-flex items-center gap-2 self-start rounded-full border border-white/25 text-background px-7 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300 hover:bg-highlight hover:text-foreground hover:border-highlight"
        >
          Order Online
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>
      </div>
    </div>

    {/* Auto-sliding marquee track */}
    <div className="group relative">
      <div className="flex gap-5 w-max animate-marquee group-hover:[animation-play-state:paused] motion-reduce:animate-none">
        {[...ITEMS, ...ITEMS].map((item, i) => (
          <Slide key={i} item={item} />
        ))}
      </div>
      {/* edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-[#241812] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-[#241812] to-transparent" />
    </div>
  </section>
  );
};

export default SignatureSlider;
